"use client";

import {
  ContentfulImageConfig,
  isTypeContent_full,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeContent_liteWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { renderEmbeddedEntry, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import type {
  BodyProps,
  BodyVariants,
  ButtonGroupProps,
  HeadingProps,
  HeadingVariants,
  SubheadProps,
  SubheadVariants,
} from "@red-digital/bricks";
import { Body, ButtonGroup, Heading, Subhead } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { Button } from "../Button";
import { ContentfulImage } from "../ContentfulImage";
// @ts-ignore
import styles from "./ContentLayout.module.scss";

interface ContentLayoutProps {
  content?: TypeContent_fullWithoutUnresolvableLinksResponse | TypeContent_liteWithoutUnresolvableLinksResponse;
  className?: string;
  children?: React.ReactNode;
  topSlot?: React.ReactNode;
  eyebrow?: Omit<SubheadProps<"span">, "type" | "variant" | "children"> & {
    variant?: SubheadVariants;
  };
  heading?: Omit<HeadingProps, "variant" | "children"> & { variant?: HeadingVariants };
  subhead?: Omit<SubheadProps<"p">, "type" | "variant" | "children"> & {
    variant?: SubheadVariants;
  };
  body?: Omit<BodyProps<"p">, "variant" | "children"> & { variant?: BodyVariants };
  disclaimer?: Omit<BodyProps<"small">, "variant"> & { variant?: BodyVariants };
  image_aspect_ratio?: string;
  actions?: {
    disclaimer?: boolean;
    buttonsSlot?: React.ReactElement;
  };
  btnPlacement?: ButtonGroupProps["placement"];
}

interface CustomCSSProperties extends React.CSSProperties {
  "--eyebrow-color"?: string;
  "--heading-color"?: string;
  "--subhead-color"?: string;
  "--body-color"?: string;
}

export const ContentLayout: React.FC<ContentLayoutProps> = ({
  content,
  className,
  topSlot,
  heading,
  subhead,
  image_aspect_ratio,
  body,
  btnPlacement = "left",
}) => {
  const inspector = useContentfulInspectorMode();

  if (content) {
    const fields = content?.fields || {};
    const isContentFull = isTypeContent_full(content);
    const style: CustomCSSProperties = {};

    const getCookie = (name: string) => {
      if (typeof window !== "undefined") {
        if (window?.document?.cookie) {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);

          if (parts?.length === 2) {
            return parts?.pop()?.split(";")[0];
          }
        }
      }

      return "";
    };

    const hasBtnLogos =
      isContentFull && content?.fields?.buttons?.some((button) => button?.fields?.logo?.fields?.file?.url);
    const hasButtons = isContentFull && !!content?.fields?.buttons?.length;
    const hasEyebrow = isContentFull && content?.fields?.eyebrow && typeof content?.fields?.eyebrow === "object";
    const hasContent = !!(hasEyebrow || fields.heading || fields.subhead || fields.body);
    const imagePosition = fields.image_position || "Top";
    const imagePositionMobile = fields.image_position_mobile || "Top";

    if (isContentFull && content?.fields?.eyebrow_color) {
      style["--eyebrow-color"] = `var(${content.fields.eyebrow_color})`;
    }
    if (fields.heading_color) style["--heading-color"] = `var(${content.fields.heading_color})`;
    if (fields.subhead_color) style["--subhead-color"] = `var(${content.fields.subhead_color})`;
    if (fields.body_color) style["--body-color"] = `var(${content.fields.body_color})`;

    const handleExternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      EventClient.elementClicked({
        webElement: {
          elementType: "Landing Page",
          htmlId: event.currentTarget.href,
          location: fields.name || "",
          name: event.currentTarget.innerText,
          position: "",
          text: "",
        },
      });
    };

    return (
      <div className={cn(styles.content, className)} style={style}>
        <div
          className={cn(styles.contentLayout, {
            [styles[`desktopImage${imagePosition}`]]: imagePosition,
            [styles[`mobileImage${imagePositionMobile}`]]: imagePositionMobile,
            [styles.hasButtons]: isContentFull && !!content?.fields?.buttons?.length,
          })}
        >
          {fields.image && (
            <div
              className={cn(styles.imageContainer, {
                [styles[`image${imagePosition}`]]: imagePosition,
                [styles[`imageMobile${imagePositionMobile}`]]: imagePositionMobile,
              })}
            >
              <ContentfulImage
                image={content.fields.image}
                config={content.fields.image_config as unknown as ContentfulImageConfig}
                className={cn(styles.image, {
                  [styles.imageTopFull]: isContentFull && imagePosition === "Top",
                })}
                {...inspector({
                  entryId: content.sys.id,
                  fieldId: "image",
                })}
                loading="eager"
                style={image_aspect_ratio ? { aspectRatio: image_aspect_ratio } : {}}
              />
            </div>
          )}
          {hasContent && (
            <div className={styles.contentContainer}>
              <div className={styles.content}>
                {topSlot}
                {isContentFull &&
                  content?.fields?.eyebrow &&
                  typeof content?.fields?.eyebrow === "object" &&
                  documentToReactComponents(content?.fields?.eyebrow, {
                    renderMark,
                    renderText,
                    renderNode: {
                      [BLOCKS.PARAGRAPH]: (node, children) => (
                        <Subhead
                          type="span"
                          className={styles.eyebrow}
                          variant="subhead-sm"
                          modifier={500}
                          {...inspector({
                            entryId: content.sys.id,
                            fieldId: "eyebrow",
                          })}
                        >
                          {children}
                        </Subhead>
                      ),
                      [INLINES.EMBEDDED_ENTRY]: (node) =>
                        renderEmbeddedEntry(node, "Content Layout Eyebrow", content?.fields?.eyebrow),
                    },
                  })}
                {fields.heading &&
                  typeof fields.heading === "object" &&
                  documentToReactComponents(fields.heading, {
                    renderNode: {
                      [BLOCKS.PARAGRAPH]: (node, children) => (
                        <Heading
                          variant="heading-2xl"
                          modifier={700}
                          {...heading}
                          className={cn(styles.heading, heading?.className)}
                          {...inspector({
                            entryId: content.sys.id,
                            fieldId: "heading",
                          })}
                        >
                          {children}
                        </Heading>
                      ),
                      [INLINES.EMBEDDED_ENTRY]: (node) =>
                        renderEmbeddedEntry(node, "Content Layout Heading", fields.heading, "md"),
                    },
                    renderText: (text) => {
                      const regex = /{{first_name}}/g;
                      const regexWithComma = /, {{first_name}}/g;

                      if (regexWithComma.test(text)) {
                        const name = getCookie("taxi-first_name");
                        return text.replace(regexWithComma, name ? `, ${name}` : "");
                      } else if (regex.test(text)) {
                        return text.replace(regex, getCookie("taxi-first_name") || "");
                      }

                      return text;
                    },
                  })}
                {fields.subhead &&
                  typeof fields.subhead === "object" &&
                  documentToReactComponents(fields.subhead, {
                    renderMark,
                    renderText,
                    renderNode: {
                      [BLOCKS.PARAGRAPH]: (node, children) => (
                        <Subhead
                          type="p"
                          variant="subhead-md"
                          modifier={500}
                          {...subhead}
                          className={cn(styles.subhead, subhead?.className)}
                          {...inspector({
                            entryId: content.sys.id,
                            fieldId: "subhead",
                          })}
                        >
                          {children}
                        </Subhead>
                      ),
                      [INLINES.EMBEDDED_ENTRY]: (node) =>
                        renderEmbeddedEntry(node, "Content Layout Subhead", fields.subhead),
                    },
                  })}
                {fields.body &&
                  typeof fields.body === "object" &&
                  documentToReactComponents(fields.body, {
                    renderMark,
                    renderText,
                    renderNode: {
                      [BLOCKS.PARAGRAPH]: (node, children) => (
                        <Body className={styles.body} variant="body-md" {...body}>
                          {children}
                        </Body>
                      ),
                      [INLINES.EMBEDDED_ENTRY]: (node) => renderEmbeddedEntry(node, "Content Layout Body", fields.body),
                      [INLINES.HYPERLINK]: (node: any) => {
                        const { uri } = node?.data;
                        const content = node?.content?.[0];
                        if (content && "value" in content) {
                          return (
                            <a href={uri} target="_blank" rel="noopener noreferrer" onClick={handleExternalLinkClick}>
                              {content.value}
                            </a>
                          );
                        }
                        return null;
                      },
                    },
                  })}
              </div>
            </div>
          )}
          {isContentFull && content?.fields?.buttons && (
            <ButtonGroup
              className={cn(styles.actions, styles.buttonsContainer, { [styles.btnLogos]: hasBtnLogos })}
              layout="inline"
              placement={btnPlacement}
            >
              {content.fields.buttons.map((button) => (
                <Button key={button?.sys?.id} {...button?.fields}>
                  {button?.fields?.label}
                </Button>
              ))}
            </ButtonGroup>
          )}
        </div>
      </div>
    );
  }

  return null;
};
